/*
  header-mobile-nav.js
  Tracks horizontal overflow of the mobile quick-nav row and exposes it as
  `data-overflow-start` / `data-overflow-end` attributes, which the stylesheet
  uses to fade each edge and reveal the matching scroll arrow.
*/

if (!customElements.get('header-mobile-nav')) {
  class HeaderMobileNav extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('.header-mobile-nav__scroller');
      if (!this.scroller) return;

      this.arrows = this.querySelectorAll('.header-mobile-nav__arrow');
      this.frame = null;
      this.initialized = false;

      this.onScroll = this.handleScroll.bind(this);
      this.onArrowClick = this.handleArrowClick.bind(this);

      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.arrows.forEach((arrow) => arrow.addEventListener('click', this.onArrowClick));

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.scroller);
      } else {
        window.addEventListener('resize', this.onScroll, { passive: true });
      }

      this.update();
    }

    disconnectedCallback() {
      this.scroller?.removeEventListener('scroll', this.onScroll);
      this.arrows?.forEach((arrow) => arrow.removeEventListener('click', this.onArrowClick));
      window.removeEventListener('resize', this.onScroll);
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    handleScroll() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      // Hidden until the header turns solid on transparent pages, so measuring
      // has to wait for the row to get a width.
      if (!this.scroller.clientWidth) return;

      // The row always starts at the first link — the browser can otherwise
      // restore a previous scroll offset on a back/forward navigation.
      if (!this.initialized) {
        this.initialized = true;
        this.scroller.scrollLeft = 0;
      }

      // `scrollLeft` is negative in RTL, so compare on distance travelled.
      const travelled = Math.abs(this.scroller.scrollLeft);
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      const threshold = 4;

      this.toggleAttribute('data-overflow-start', travelled > threshold);
      this.toggleAttribute('data-overflow-end', maxScroll > threshold && travelled < maxScroll - threshold);
    }

    handleArrowClick(event) {
      const step = event.currentTarget.dataset.direction === 'prev' ? -1 : 1;
      const flow = document.documentElement.dir === 'rtl' ? -1 : 1;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.scroller.scrollBy({
        left: this.scroller.clientWidth * 0.7 * step * flow,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
  }

  customElements.define('header-mobile-nav', HeaderMobileNav);
}
